// src/components/profile/Profile.tsx

import React, { useState } from 'react';   // ← добавить useState
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Grid,
    Divider,
    Chip,
    Button,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Telegram as TelegramIcon,
    Badge as BadgeIcon,
    Logout as LogoutIcon,
    Shield as ShieldIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ChangePasswordDialog from './ChangePasswordDialog';

const Profile: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    // ← НОВОЕ состояние
    const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
    if (!user) {
        return null;
    }

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    // Получить инициалы для аватара
    const getInitials = (fullName: string): string => {
        const parts = fullName.trim().split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        if (parts.length === 1) {
            return parts[0][0].toUpperCase();
        }
        return '?';
    };

    // Перевести роль на русский
    const getRoleName = (role: string): string => {
        switch (role) {
            case 'ROLE_ADMIN':
                return 'Администратор';
            case 'ROLE_TEACHER':
                return 'Репетитор';
            case 'ROLE_USER':
                return 'Пользователь';
            default:
                return role;
        }
    };

    // Получить цвет роли
    const getRoleColor = (role: string): 'error' | 'primary' | 'default' => {
        switch (role) {
            case 'ROLE_ADMIN':
                return 'error';
            case 'ROLE_TEACHER':
                return 'primary';
            default:
                return 'default';
        }
    };

    return (
        <Box>
            {/* Заголовок */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    component="h1"
                    sx={{ fontWeight: 500 }}
                >
                    👤 Профиль
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Информация о вашем аккаунте
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Карточка с аватаром и именем */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper
                        sx={{
                            p: 3,
                            textAlign: 'center',
                            height: '100%',
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 100,
                                height: 100,
                                mx: 'auto',
                                mb: 2,
                                bgcolor: 'primary.main',
                                fontSize: '2.5rem',
                                fontWeight: 600,
                            }}
                        >
                            {getInitials(user.fullName)}
                        </Avatar>

                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {user.fullName}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            @{user.phoneNumber}
                        </Typography>

                        {/* Роли */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap', mb: 3 }}>
                            {user.roles.map((role) => (
                                <Chip
                                    key={role}
                                    icon={<ShieldIcon />}
                                    label={getRoleName(role)}
                                    size="small"
                                    color={getRoleColor(role)}
                                />
                            ))}
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Кнопки */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<LockIcon />}
                                onClick={() => setChangePasswordDialogOpen(true)}
                            >
                                Изменить пароль
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                startIcon={<LogoutIcon />}
                                onClick={handleLogout}
                            >
                                Выйти из аккаунта
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Карточка с контактной информацией */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Контактная информация
                        </Typography>

                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>

                            {/* Full Name */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                                        <BadgeIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            ФИО
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {user.fullName}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Email */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                                        <EmailIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Email
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {user.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Phone */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                                        <PhoneIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Телефон
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {user.phoneNumber}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Telegram */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                                        <TelegramIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Telegram
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {user.telegram}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
            {/* ← НОВОЕ: диалог смены пароля */}
            <ChangePasswordDialog
                open={changePasswordDialogOpen}
                onClose={() => setChangePasswordDialogOpen(false)}
            />
        </Box>
    );
};

export default Profile;